import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { TypographyH4, TypographyP2 } from '@heidi/design-system/typography';
import { Check, TriangleAlert, UserRound } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { inviteTeamStepMessages } from '../steps/invite-team-step.messages';
import { SkeletonBlock, SkeletonLine } from './PreviewPanelContent';

/** Fixed row slots so the card height stays stable below this count. */
const ROW_SLOTS = 5;
/** Avatar md (32px) × 5 + gap-5 (20px) × 4 — keep both modes on this height. */
const LIST_VIEWPORT_HEIGHT_CLASS = 'h-[240px]';

const STATUS_BADGE_CLASS =
  'flex size-6 shrink-0 items-center justify-center rounded-full';

const EMAIL_SKELETON_WIDTHS = ['w-40', 'w-36', 'w-44', 'w-32', 'w-40'] as const;

/** Same regex as `useValidateEmail` / InputEmails chips. */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

type InviteTeamPreviewProps = {
  clinicName?: string;
  inviteEmails: string[];
};

function OrgHeader({
  clinicName,
  clinicianCount,
}: {
  clinicName?: string;
  clinicianCount: number;
}) {
  const hasClinicName = Boolean(clinicName?.trim());

  return (
    <div className="flex items-center gap-3">
      {/* Squircle mint tile — matches Avatar square radius (30%). */}
      <div
        className="flex size-10 shrink-0 items-center justify-center rounded-[30%] bg-entity-green"
        aria-hidden
      >
        <HeidiLogoMark
          artwork="standard"
          className="size-6 text-entity-green-foreground"
          data-testid="invite-team-heidi-mark"
        />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        {hasClinicName ? (
          // H4 size + Exposure face (H2/H3 Exposure, but sized like Figma).
          <TypographyH4 className="truncate font-exposure text-text-primary">
            {clinicName}
          </TypographyH4>
        ) : (
          <SkeletonBlock width="w-40" variant="muted" />
        )}
        <TypographyP2 className="text-text-secondary">
          <FormattedMessage
            {...inviteTeamStepMessages.cliniciansCount}
            values={{ count: clinicianCount }}
          />
        </TypographyP2>
      </div>
    </div>
  );
}

function RowStatus({ email }: { email?: string }) {
  if (!email) return null;
  if (!isValidEmail(email)) {
    return (
      <div
        className={`${STATUS_BADGE_CLASS} bg-validation-destructive-muted`}
        aria-hidden
      >
        <TriangleAlert
          className="size-4 text-validation-destructive"
          strokeWidth={2.25}
        />
      </div>
    );
  }
  return (
    <div className={`${STATUS_BADGE_CLASS} bg-validation-success-muted`} aria-hidden>
      <Check className="size-3.5 text-validation-success" strokeWidth={2.5} />
    </div>
  );
}

function MemberRow({
  email,
  emailSkeletonWidth,
}: {
  email?: string;
  emailSkeletonWidth: string;
}) {
  return (
    <div className="flex h-8 items-center gap-3">
      <Avatar
        size="md"
        color={email ? getAvatarColor(email) : 'empty'}
        shape="circle"
        className="shrink-0"
      >
        <AvatarFallback icon={<UserRound />} />
      </Avatar>
      <div className="flex min-w-0 flex-1 items-center">
        {email ? (
          <TypographyP2 className="truncate text-text-secondary">{email}</TypographyP2>
        ) : (
          <SkeletonLine width={emailSkeletonWidth} variant="muted" />
        )}
      </div>
      <RowStatus email={email} />
    </div>
  );
}

/**
 * Right-panel illustration for invite-team: org header + clinician rows.
 * Pads to {@link ROW_SLOTS} when fewer emails; scrolls when more.
 */
export function InviteTeamPreview({ clinicName, inviteEmails }: InviteTeamPreviewProps) {
  const clinicianCount = inviteEmails.length;
  const isScrollable = clinicianCount > ROW_SLOTS;

  const rows: Array<string | undefined> = isScrollable
    ? inviteEmails
    : [
        ...inviteEmails,
        ...Array.from({ length: ROW_SLOTS - clinicianCount }, () => undefined),
      ];

  return (
    <div
      className="flex size-full items-center justify-center overflow-hidden"
      data-testid="invite-team-preview"
    >
      <div className="flex w-[28rem] flex-col gap-7 overflow-hidden rounded-3xl bg-surface p-8 shadow-lg">
        <OrgHeader clinicName={clinicName} clinicianCount={clinicianCount} />

        <div
          className={
            isScrollable
              ? // -me-8 pulls thumb into card padding so it sits flush on the
                // card edge; pe-8 keeps row content aligned with the header.
                `scrollbar-subtle -me-8 flex ${LIST_VIEWPORT_HEIGHT_CLASS} flex-col gap-5 overflow-y-scroll pe-8`
              : `flex ${LIST_VIEWPORT_HEIGHT_CLASS} flex-col gap-5`
          }
          data-testid="invite-team-preview-list"
        >
          {rows.map((email, index) => (
            <MemberRow
              key={email ?? `placeholder-${index}`}
              email={email}
              emailSkeletonWidth={
                EMAIL_SKELETON_WIDTHS[index % EMAIL_SKELETON_WIDTHS.length] ?? 'w-36'
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
